import { useState, useEffect } from "react";
import { ELECTORAL_POSITIONS, ROLE_GEO_RULES } from "@qm/shared/constants";
import { DIAL_CODES, getStatesForCountry, getLgasForState } from "../../utils/phone.js";
import { saveDraft, submitDetails } from "../../services/persona-owner.js";
import { F, L } from "../../styles/theme.js";

const GEO_COUNTRIES = [
  { code: "NG", label: "\ud83c\uddf3\ud83c\uddec Nigeria" },
  { code: "US", label: "\ud83c\uddfa\ud83c\uddf8 United States" },
  { code: "GB", label: "\ud83c\uddec\ud83c\udde7 United Kingdom" },
  { code: "ZA", label: "\ud83c\uddff\ud83c\udde6 South Africa" },
  { code: "GH", label: "\ud83c\uddec\ud83c\udded Ghana" },
  { code: "KE", label: "\ud83c\uddf0\ud83c\uddea Kenya" },
];

/**
 * ONB-P1: Claim details form.
 * Persona owner fills in elected official info before verification.
 *
 * Props:
 *   application — current application object
 *   onUpdate    — (updatedApp) => void
 *   onNext      — () => void — navigate to verification step
 */
export function ClaimDetails({ application, onUpdate, onNext }) {
  // v1.27.0: party field removed from the PO claim form. Existing applications
  // that already have a `party` value retain it in the DB (column unchanged),
  // we just stop asking new applicants for it and stop sending it in draft /
  // submit payloads.
  const [name, setName] = useState(application?.official_name ?? "");
  const [position, setPosition] = useState(application?.electoral_position ?? "");
  const [country, setCountry] = useState(application?.geo_country ?? "NG");
  const [state, setState] = useState(application?.geo_state ?? "");
  const [lga, setLga] = useState(application?.geo_lga ?? "");
  const [bio, setBio] = useState(application?.bio ?? "");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const states = getStatesForCountry(country);
  const lgas = state ? getLgasForState(country, state) : [];
  const geoRule = ROLE_GEO_RULES[position];

  const canSubmit = name.trim() && position && bio.trim() &&
    (!geoRule?.country || country) &&
    (!geoRule?.state || state);

  // Auto-save draft every 30s
  useEffect(() => {
    if (!application?.id || application.status !== "pending_details") return;
    const timer = setInterval(async () => {
      try {
        const { application: updated } = await saveDraft({
          official_name: name, electoral_position: position,
          geo_country: country, geo_state: state, geo_lga: lga, bio,
        });
        onUpdate?.(updated);
      } catch { /* non-fatal */ }
    }, 30000);
    return () => clearInterval(timer);
  }, [name, position, country, state, lga, bio, application?.id, application?.status]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const { application: updated } = await saveDraft({
        official_name: name, electoral_position: position,
        geo_country: country, geo_state: state, geo_lga: lga, bio,
      });
      onUpdate?.(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const { application: updated } = await submitDetails({
        official_name: name, electoral_position: position,
        geo_country: country, geo_state: state, geo_lga: lga, bio,
      });
      onUpdate?.(updated);
      onNext?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ maxWidth: 640, margin: "0 auto", padding: "24px 28px" }}>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: "#f1f5f9", fontFamily: "Georgia, serif", marginBottom: 8 }}>
        Claim Your Profile
      </h1>
      <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 28, lineHeight: 1.6 }}>
        Tell us about the elected official you represent. This information will be used to create their digital clone on QuestionMe.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {/* Official Name */}
        <div>
          <label style={L}>Elected Official's Full Name *</label>
          <input style={F} value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. Senator Jane Smith" />
        </div>

        {/* Electoral Position */}
        <div>
          <label style={L}>Electoral Position *</label>
          <select style={F} value={position} onChange={e => { setPosition(e.target.value); setState(""); setLga(""); }}>
            <option value="">Select position...</option>
            {ELECTORAL_POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        {/* v1.27.0: Party field removed from the claim form. Existing
            applications with a `party` value keep it in the DB; new drafts
            and submissions simply don't set it. */}

        {/* Geo Scope */}
        <div>
          <label style={L}>Country *</label>
          <select style={F} value={country} onChange={e => { setCountry(e.target.value); setState(""); setLga(""); }}>
            {GEO_COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </div>

        {geoRule?.state && states.length > 0 && (
          <div>
            <label style={L}>State / Province *</label>
            <select style={F} value={state} onChange={e => { setState(e.target.value); setLga(""); }}>
              <option value="">Select state...</option>
              {states.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}

        {geoRule?.lga && lgas.length > 0 && (
          <div>
            <label style={L}>Local Government Area</label>
            <select style={F} value={lga} onChange={e => setLga(e.target.value)}>
              <option value="">Select LGA...</option>
              {lgas.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        )}

        {/* Bio */}
        <div>
          <label style={L}>Profile Bio *</label>
          <textarea
            style={{ ...F, minHeight: 120, resize: "vertical" }}
            value={bio}
            onChange={e => setBio(e.target.value)}
            placeholder="Brief background — career highlights, constituency, key achievements..."
          />
          <div style={{ color: "#64748b", fontSize: 11, marginTop: 4 }}>{bio.length} characters</div>
        </div>
      </div>

      {error && (
        <div style={{ color: "#ef4444", fontSize: 13, marginTop: 16, padding: "10px 14px", background: "#ef444415", borderRadius: 8 }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: 12, marginTop: 28 }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: "12px 24px", borderRadius: 8, border: "1px solid #2d3f5a",
            background: "#1e2d45", color: "#94a3b8", fontSize: 14,
            cursor: saving ? "wait" : "pointer", fontFamily: "inherit",
          }}
        >
          {saving ? "Saving..." : saved ? "Saved \u2713" : "Save Draft"}
        </button>

        <button
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          style={{
            flex: 1, padding: "12px 24px", borderRadius: 8, border: "none",
            background: canSubmit ? "#3b82f6" : "#1e2d45",
            color: canSubmit ? "#fff" : "#64748b",
            fontSize: 14, fontWeight: 600, cursor: canSubmit ? "pointer" : "not-allowed",
            fontFamily: "inherit",
          }}
        >
          {submitting ? "Submitting..." : "Continue to Verification \u2192"}
        </button>
      </div>
    </div>
  );
}
